import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useContext, useMemo, useState } from 'react';

import { AdditionalDefault, fromTree, TreeCollection } from '@hh.ru/magritte-ui';

export interface OnChangeUpdater {
    (prev: { value: string[]; excludedValue?: string[] }): {
        value: string[];
        excludedValue?: string[];
    };
}

interface CompositeSelectionContextValues {
    collection: TreeCollection<AdditionalDefault>;
    value: string[];
    isTreeSelectorModalVisible: boolean;
    setTreeSelectorModalVisibility: Dispatch<SetStateAction<boolean>>;
    onChange: (updater: OnChangeUpdater) => void;
    excludedValue?: string[];
}

export type CompositeSelectionContextProps = Omit<
    CompositeSelectionContextValues,
    'isTreeSelectorModalVisible' | 'setTreeSelectorModalVisibility' | 'onChange'
> & { onChange: (value: string[], excludedValue?: string[]) => void };

const CompositeSelectionContext = createContext<CompositeSelectionContextValues>({
    collection: fromTree([]),
    value: [],
    isTreeSelectorModalVisible: false,
    setTreeSelectorModalVisibility: () => null,
    onChange: () => null,
    excludedValue: [],
});

export const CompositeSelectionContextProvider: FC<CompositeSelectionContextProps & PropsWithChildren> = (props) => {
    const [isTreeSelectorModalVisible, setTreeSelectorModalVisibility] = useState(false);

    const value = useMemo(
        () => ({
            collection: props.collection,
            value: props.value,
            isTreeSelectorModalVisible,
            setTreeSelectorModalVisibility,
            onChange: (updater: OnChangeUpdater) => {
                const { value, excludedValue = props.excludedValue } = updater({
                    value: props.value,
                    excludedValue: props.excludedValue,
                });

                props.onChange(value, excludedValue ?? props.excludedValue);
            },
            excludedValue: props.excludedValue ?? [],
        }),
        [props, isTreeSelectorModalVisible]
    );

    return <CompositeSelectionContext.Provider value={value}>{props.children}</CompositeSelectionContext.Provider>;
};

export const useCompositeSelectionContext = (): CompositeSelectionContextValues =>
    useContext<CompositeSelectionContextValues>(CompositeSelectionContext);
